import { Controller } from "@hotwired/stimulus"

// Site selection on the capacity board's Raster, for "Ausgewählte beauftragen". ONE controller on
// the board root (#planning_board) — never one per site section: per-node controllers are this
// board's recurring perf trap. The selected ids live here rather than in the checkboxes, so a
// header that is re-rendered (or connects late) picks its ticked state back up.
//
// The bar's link carries the selection as site_ids[] and opens the multi-site dialog as a Turbo
// Stream; a successful submit of that dialog clears the selection.
export default class extends Controller {
  static targets = ["checkbox", "bar", "count", "dispatchLink", "limitHint"]
  static values = { url: String, max: Number }

  initialize() {
    this.selected = new Set()
  }

  connect() {
    this.render()
  }

  checkboxTargetConnected(checkbox) {
    checkbox.checked = this.selected.has(checkbox.value)
  }

  toggle(event) {
    const { value, checked } = event.target
    if (checked) this.selected.add(value)
    else this.selected.delete(value)
    this.render()
  }

  // "Visible" = rendered and not hidden by a client-side filter (no offsetParent when hidden).
  selectAll() {
    this.checkboxTargets
      .filter((checkbox) => checkbox.offsetParent !== null)
      .forEach((checkbox) => this.selected.add(checkbox.value))
    this.render()
  }

  clear() {
    this.selected.clear()
    this.render()
  }

  // The link stays in the DOM while over the limit (or empty); a click must not open the dialog.
  guard(event) {
    if (!this.dispatchable) event.preventDefault()
  }

  // turbo:submit-end@document — only the multi-site dialog's form clears the selection.
  submitted(event) {
    if (!event.detail?.success) return
    if (!event.target?.matches?.("[data-sites-dispatch-form]")) return

    this.clear()
  }

  get dispatchable() {
    return this.selected.size > 0 && this.selected.size <= this.maxValue
  }

  render() {
    this.checkboxTargets.forEach((checkbox) => {
      checkbox.checked = this.selected.has(checkbox.value)
    })
    if (this.hasCountTarget) this.countTarget.textContent = String(this.selected.size)
    if (this.hasBarTarget) {
      this.barTarget.classList.toggle("hidden", this.selected.size === 0)
      this.barTarget.classList.toggle("flex", this.selected.size > 0)
    }
    if (this.hasLimitHintTarget) {
      this.limitHintTarget.classList.toggle("hidden", this.selected.size <= this.maxValue)
    }
    if (this.hasDispatchLinkTarget) this.renderLink()
  }

  renderLink() {
    const url = new URL(this.urlValue, window.location.origin)
    this.selected.forEach((id) => url.searchParams.append("site_ids[]", id))
    this.dispatchLinkTarget.href = `${url.pathname}${url.search}`

    if (this.dispatchable) this.dispatchLinkTarget.removeAttribute("aria-disabled")
    else this.dispatchLinkTarget.setAttribute("aria-disabled", "true")
  }
}
